import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { ArrowLeft } from "lucide-react";
import type { AdminUserDetail as AdminUserDetailType } from "@guestbook/shared";
import { Container } from "@/shared/components/layout/Container";
import { Section } from "@/shared/components/layout/Section";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shared/components/ui/AlertDialog";
import { Badge } from "@/shared/components/ui/Badge";
import { Button } from "@/shared/components/ui/Button";
import { Checkbox } from "@/shared/components/ui/Checkbox";
import { FormField } from "@/shared/components/form/FormField";
import { Label } from "@/shared/components/ui/Label";
import { useMe } from "@/shared/lib/auth";
import {
  useAdminUser,
  useDeleteAdminUser,
  useSuspendUser,
  useUnsuspendUser,
  useUpdateAdminUser,
} from "@/shared/lib/admin";

export function AdminUserDetail() {
  const { id } = useParams<{ id: string }>();
  const query = useAdminUser(id);
  const me = useMe();

  return (
    <Section spacing="md">
      <Container size="2xl">
        <div className="flex flex-col gap-8">
          <Link
            to="/app/admin/users"
            className="inline-flex items-center gap-1 type-label-sm tracking-wider uppercase text-text-muted hover:text-text"
          >
            <ArrowLeft aria-hidden className="h-3.5 w-3.5" />
            Users
          </Link>

          {query.isLoading ? (
            <p className="type-body-base text-text-muted">Loading…</p>
          ) : query.isError ? (
            <p className="type-body-base text-danger-strong">
              Couldn't load this user: {query.error.message}
            </p>
          ) : query.data ? (
            <UserDetailView
              user={query.data}
              isSelf={me.data?.id === query.data.id}
            />
          ) : null}
        </div>
      </Container>
    </Section>
  );
}

function UserDetailView({
  user,
  isSelf,
}: {
  user: AdminUserDetailType;
  isSelf: boolean;
}) {
  return (
    <>
      <div className="flex flex-col gap-3">
        <h1 className="type-heading-lg text-text break-all">{user.email}</h1>
        <div className="flex items-center gap-2">
          {user.suspendedAt ? (
            <Badge className="text-danger-strong">Suspended</Badge>
          ) : (
            <Badge variant="muted">Active</Badge>
          )}
          {user.isAdmin ? <Badge>Admin</Badge> : null}
          {isSelf ? <Badge variant="muted">You</Badge> : null}
        </div>
      </div>

      <section className="flex flex-col gap-6 rounded-2xl glass-surface p-6">
        <h2 className="type-heading-sm text-text">Profile</h2>
        <EditProfileForm user={user} isSelf={isSelf} />
      </section>

      <section className="flex flex-col gap-4 rounded-2xl glass-surface p-6">
        <h2 className="type-heading-sm text-text">Owned data</h2>
        <dl className="grid grid-cols-3 gap-4">
          <CountStat label="Contacts" value={user.counts.contacts} />
          <CountStat label="Households" value={user.counts.households} />
          <CountStat label="Lists" value={user.counts.lists} />
        </dl>
      </section>

      <section className="flex flex-col gap-4 rounded-2xl glass-surface p-6">
        <div className="flex flex-col gap-1">
          <h2 className="type-heading-sm text-text">Account actions</h2>
          <p className="type-body-sm text-text-muted">
            {isSelf
              ? "You can't suspend or delete your own account."
              : user.suspendedAt
              ? "This account is suspended — the user can't sign in."
              : "Suspending signs the user out everywhere and blocks new logins."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {user.suspendedAt ? (
            <UnsuspendButton user={user} />
          ) : (
            <SuspendButton user={user} disabled={isSelf} />
          )}
          <DeleteButton user={user} disabled={isSelf} />
        </div>
      </section>
    </>
  );
}

function CountStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="type-label-sm tracking-wider uppercase text-text-muted">
        {label}
      </dt>
      <dd className="type-heading-sm text-text">{value}</dd>
    </div>
  );
}

function EditProfileForm({
  user,
  isSelf,
}: {
  user: AdminUserDetailType;
  isSelf: boolean;
}) {
  const update = useUpdateAdminUser();
  const [name, setName] = useState(user.name ?? "");
  const [email, setEmail] = useState(user.email);
  const [isAdmin, setIsAdmin] = useState(user.isAdmin);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    update.mutate({
      id: user.id,
      body: {
        name: name.trim() || null,
        email: email.trim(),
        isAdmin,
      },
    });
  }

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
      <FormField
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <FormField
        label="Email"
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <div className="mx-4 flex items-center gap-3">
        <Checkbox
          id="isAdmin"
          checked={isAdmin}
          disabled={isSelf}
          onCheckedChange={(v) => setIsAdmin(v === true)}
        />
        <Label htmlFor="isAdmin">Admin access</Label>
      </div>
      {isSelf ? (
        <p className="mx-4 type-body-sm text-text-muted">
          You can't remove your own admin access.
        </p>
      ) : null}

      {update.isError ? (
        <p className="mx-4 type-body-sm text-danger-strong">
          {update.error.message}
        </p>
      ) : null}
      {update.isSuccess ? (
        <p className="mx-4 type-body-sm text-success">Saved.</p>
      ) : null}

      <div className="flex items-center justify-end">
        <Button type="submit" disabled={update.isPending || !email.trim()}>
          {update.isPending ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </form>
  );
}

function SuspendButton({
  user,
  disabled,
}: {
  user: AdminUserDetailType;
  disabled: boolean;
}) {
  const suspend = useSuspendUser();
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="destructive" disabled={disabled}>
          Suspend
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Suspend this account?</AlertDialogTitle>
          <AlertDialogDescription>
            {user.email} will be signed out everywhere and blocked from signing
            in until you unsuspend them. Their data is kept.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {suspend.isError ? (
          <p className="type-body-sm text-danger-strong">
            {suspend.error.message}
          </p>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogAction asChild>
            <Button
              variant="destructive"
              disabled={suspend.isPending}
              onClick={(e) => {
                e.preventDefault();
                suspend.mutate(user.id);
              }}
            >
              {suspend.isPending ? "Suspending…" : "Suspend"}
            </Button>
          </AlertDialogAction>
          <AlertDialogCancel asChild>
            <Button variant="ghost">Cancel</Button>
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function UnsuspendButton({ user }: { user: AdminUserDetailType }) {
  const unsuspend = useUnsuspendUser();
  return (
    <Button
      variant="secondary"
      disabled={unsuspend.isPending}
      onClick={() => unsuspend.mutate(user.id)}
    >
      {unsuspend.isPending ? "Restoring…" : "Unsuspend"}
    </Button>
  );
}

function DeleteButton({
  user,
  disabled,
}: {
  user: AdminUserDetailType;
  disabled: boolean;
}) {
  const navigate = useNavigate();
  const remove = useDeleteAdminUser();
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="ghost-danger" disabled={disabled}>
          Delete
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this account?</AlertDialogTitle>
          <AlertDialogDescription>
            This permanently deletes {user.email} and <strong>all</strong> of
            their data — contacts, households, lists, and share links. This
            cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {remove.isError ? (
          <p className="type-body-sm text-danger-strong">
            {remove.error.message}
          </p>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogAction asChild>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={(e) => {
                e.preventDefault();
                remove.mutate(user.id, {
                  onSuccess: () => navigate("/app/admin/users"),
                });
              }}
            >
              {remove.isPending ? "Deleting…" : "Delete account"}
            </Button>
          </AlertDialogAction>
          <AlertDialogCancel asChild>
            <Button variant="ghost">Cancel</Button>
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
